import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

// Contact Profile interface
interface ContactProfile {
  contact_id: number;
  profile_name: string;
  name_first: string;
  name_last: string;
  email: string;
  phone: string;
  address_line1: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
  is_default: boolean;
}

interface ContactProfileManagerProps {
  projectId: number;
  onProfileSelect?: (profile: ContactProfile) => void;
}

const ContactProfileManager: React.FC<ContactProfileManagerProps> = ({
  projectId,
  onProfileSelect
}) => {
  // State management
  const [profiles, setProfiles] = useState<ContactProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState<ContactProfile | null>(null);
  const [formData, setFormData] = useState<Partial<ContactProfile>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [settingDefault, setSettingDefault] = useState<number | null>(null);

  // Form validation
  const validateForm = (data: Partial<ContactProfile>): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!data.profile_name) errors.profile_name = 'Profile name is required';
    if (!data.name_first) errors.name_first = 'First name is required';
    if (!data.name_last) errors.name_last = 'Last name is required';

    if (!data.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.email = 'Invalid email format';
    }

    if (!data.phone) {
      errors.phone = 'Phone is required';
    } else if (!/^\+?[\d\s\-()]+$/.test(data.phone) || data.phone.length < 5) {
      errors.phone = 'Invalid phone format';
    }

    if (!data.address_line1) errors.address_line1 = 'Address is required';
    if (!data.city) errors.city = 'City is required';
    if (!data.state_province) errors.state_province = 'State/Province is required';
    if (!data.postal_code) errors.postal_code = 'Postal code is required';
    if (!data.country) errors.country = 'Country is required';

    return errors;
  };

  // Load profiles on mount
  const loadProfiles = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await fetch(`/api/v1/projects/${projectId}/contacts`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        let errorMessage = 'Failed to load contact profiles';
        if (response.status === 401) {
          errorMessage = 'Authentication required';
        } else if (response.status === 404) {
          errorMessage = 'Project not found';
        } else {
          const data = await response.json();
          errorMessage = data.detail || 'Failed to load contact profiles';
        }
        setError(errorMessage);
        toast.error(errorMessage);
        return;
      }

      const data = await response.json();
      setProfiles(data);
    } catch (err) {
      setError('Failed to load contact profiles');
      toast.error('Failed to load contact profiles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfiles();
  }, [projectId]);

  // Handle form changes
  const handleFormChange = (field: string, value: string) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);

    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: '' });
    }
  };

  // Handle form submit (create or update)
  const handleFormSubmit = async () => {
    // Validate form
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('auth_token');

    try {
      const isEdit = editingProfile !== null;
      const url = isEdit
        ? `/api/v1/projects/${projectId}/contacts/${editingProfile.contact_id}`
        : `/api/v1/projects/${projectId}/contacts`;

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const data = await response.json();
        // Handle FastAPI validation errors (detail is an array)
        let errorMessage = `Failed to ${isEdit ? 'update' : 'create'} profile`;
        if (Array.isArray(data.detail)) {
          errorMessage = data.detail.map((e: { msg: string; loc?: (string | number)[] }) => {
            // Get field name from loc array (last element is usually the field name)
            const field = e.loc && e.loc.length > 0 ? e.loc[e.loc.length - 1] : '';
            return field ? `${field}: ${e.msg}` : e.msg;
          }).join(', ');
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
        toast.error(errorMessage);
        return;
      }

      toast.success(`Profile ${isEdit ? 'updated' : 'created'} successfully`);
      setShowForm(false);
      setEditingProfile(null);
      setFormData({});
      setFormErrors({});
      await loadProfiles();
    } catch (err) {
      toast.error(`Failed to ${editingProfile ? 'update' : 'create'} profile`);
    } finally {
      setSaving(false);
    }
  };

  // Handle edit
  const handleEdit = (profile: ContactProfile) => {
    setEditingProfile(profile);
    setFormData({ ...profile });
    setFormErrors({});
    setShowForm(true);
  };

  // Handle add new
  const handleAddNew = () => {
    setEditingProfile(null);
    setFormData({});
    setFormErrors({});
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (profile: ContactProfile) => {
    // Prevent deletion of default profile
    if (profile.is_default) {
      toast.error('Cannot delete default profile');
      return;
    }

    setDeleteConfirmId(profile.contact_id);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!deleteConfirmId) return;

    setDeleting(true);
    const token = localStorage.getItem('auth_token');

    try {
      const response = await fetch(`/api/v1/projects/${projectId}/contacts/${deleteConfirmId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete profile');
        return;
      }

      toast.success('Profile deleted successfully');
      setDeleteConfirmId(null);
      await loadProfiles();
    } catch (err) {
      toast.error('Failed to delete profile');
    } finally {
      setDeleting(false);
    }
  };

  // Handle set default
  const handleSetDefault = async (profileId: number) => {
    setSettingDefault(profileId);
    const token = localStorage.getItem('auth_token');

    try {
      const response = await fetch(`/api/v1/projects/${projectId}/contacts/${profileId}/set-default`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        toast.error(data.detail || 'Failed to set default');
        return;
      }

      toast.success('Default profile updated');
      await loadProfiles();
    } catch (err) {
      toast.error('Failed to set default');
    } finally {
      setSettingDefault(null);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Render error state for auth errors
  if (error && (error.includes('Authentication') || error.includes('Project not found'))) {
    return (
      <div className="text-red-600 p-4">
        {error}
      </div>
    );
  }

  return (
    <div className="contact-profile-manager">
      {/* Header with Add Profile button */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Contact Profiles</h2>
        <button
          onClick={handleAddNew}
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
        >
          Add Profile
        </button>
      </div>

      {/* Error display for load errors */}
      {error && !error.includes('Authentication') && !error.includes('Project not found') && (
        <div className="text-red-600 mb-4">
          {error}
        </div>
      )}

      {/* Empty state */}
      {profiles.length === 0 && !showForm && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>No contact profiles yet.</p>
          <p>Create your first profile to get started.</p>
        </div>
      )}

      {/* Profile list */}
      {profiles.length > 0 && !showForm && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {profiles.map((profile) => (
            <div key={profile.contact_id} className="border rounded-lg p-4 bg-white dark:bg-cap-dark shadow">
              {/* Profile header with name and default badge */}
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-semibold text-lg">{profile.profile_name}</h3>
                  {profile.is_default && (
                    <span className="inline-block px-2 py-1 text-xs bg-green-600 text-white rounded">
                      Default
                    </span>
                  )}
                </div>
              </div>

              {/* Profile details */}
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                <p>{profile.name_first} {profile.name_last}</p>
                <p>{profile.email}</p>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 flex-wrap">
                {!profile.is_default && (
                  <button
                    onClick={() => handleSetDefault(profile.contact_id)}
                    disabled={settingDefault === profile.contact_id}
                    className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 disabled:opacity-50"
                  >
                    Set as Default
                  </button>
                )}
                <button
                  onClick={() => handleEdit(profile)}
                  className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(profile)}
                  className="px-3 py-1 text-sm bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-900/50"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="border rounded-lg p-6 bg-white dark:bg-cap-dark shadow-lg">
          <h3 className="text-lg font-semibold mb-4">
            {editingProfile ? 'Edit Profile' : 'New Profile'}
          </h3>

          <div className="grid gap-4 md:grid-cols-2">
            {/* Profile Name */}
            <div>
              <label htmlFor="profile_name" className="block text-sm font-medium mb-1">
                Profile Name
              </label>
              <input
                id="profile_name"
                type="text"
                value={formData.profile_name || ''}
                onChange={(e) => handleFormChange('profile_name', e.target.value)}
                className={`w-full px-3 py-2 border rounded dark:bg-gray-800 dark:text-warm-white ${
                  formErrors.profile_name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {formErrors.profile_name && (
                <p className="text-red-500 text-sm mt-1">{formErrors.profile_name}</p>
              )}
            </div>

            {/* First Name */}
            <div>
              <label htmlFor="name_first" className="block text-sm font-medium mb-1">
                First Name
              </label>
              <input
                id="name_first"
                type="text"
                value={formData.name_first || ''}
                onChange={(e) => handleFormChange('name_first', e.target.value)}
                className={`w-full px-3 py-2 border rounded dark:bg-gray-800 dark:text-warm-white ${
                  formErrors.name_first ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {formErrors.name_first && (
                <p className="text-red-500 text-sm mt-1">{formErrors.name_first}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="name_last" className="block text-sm font-medium mb-1">
                Last Name
              </label>
              <input
                id="name_last"
                type="text"
                value={formData.name_last || ''}
                onChange={(e) => handleFormChange('name_last', e.target.value)}
                className={`w-full px-3 py-2 border rounded dark:bg-gray-800 dark:text-warm-white ${
                  formErrors.name_last ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {formErrors.name_last && (
                <p className="text-red-500 text-sm mt-1">{formErrors.name_last}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={formData.email || ''}
                onChange={(e) => handleFormChange('email', e.target.value)}
                className={`w-full px-3 py-2 border rounded dark:bg-gray-800 dark:text-warm-white ${
                  formErrors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {formErrors.email && (
                <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium mb-1">
                Phone
              </label>
              <input
                id="phone"
                type="tel"
                value={formData.phone || ''}
                onChange={(e) => handleFormChange('phone', e.target.value)}
                className={`w-full px-3 py-2 border rounded dark:bg-gray-800 dark:text-warm-white ${
                  formErrors.phone ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {formErrors.phone && (
                <p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>
              )}
            </div>

            {/* Address Line 1 */}
            <div>
              <label htmlFor="address_line1" className="block text-sm font-medium mb-1">
                Address Line 1
              </label>
              <input
                id="address_line1"
                type="text"
                value={formData.address_line1 || ''}
                onChange={(e) => handleFormChange('address_line1', e.target.value)}
                className={`w-full px-3 py-2 border rounded dark:bg-gray-800 dark:text-warm-white ${
                  formErrors.address_line1 ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {formErrors.address_line1 && (
                <p className="text-red-500 text-sm mt-1">{formErrors.address_line1}</p>
              )}
            </div>

            {/* City */}
            <div>
              <label htmlFor="city" className="block text-sm font-medium mb-1">
                City
              </label>
              <input
                id="city"
                type="text"
                value={formData.city || ''}
                onChange={(e) => handleFormChange('city', e.target.value)}
                className={`w-full px-3 py-2 border rounded dark:bg-gray-800 dark:text-warm-white ${
                  formErrors.city ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {formErrors.city && (
                <p className="text-red-500 text-sm mt-1">{formErrors.city}</p>
              )}
            </div>

            {/* State/Province */}
            <div>
              <label htmlFor="state_province" className="block text-sm font-medium mb-1">
                State/Province
              </label>
              <input
                id="state_province"
                type="text"
                value={formData.state_province || ''}
                onChange={(e) => handleFormChange('state_province', e.target.value)}
                className={`w-full px-3 py-2 border rounded dark:bg-gray-800 dark:text-warm-white ${
                  formErrors.state_province ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {formErrors.state_province && (
                <p className="text-red-500 text-sm mt-1">{formErrors.state_province}</p>
              )}
            </div>

            {/* Postal Code */}
            <div>
              <label htmlFor="postal_code" className="block text-sm font-medium mb-1">
                Postal Code
              </label>
              <input
                id="postal_code"
                type="text"
                value={formData.postal_code || ''}
                onChange={(e) => handleFormChange('postal_code', e.target.value)}
                className={`w-full px-3 py-2 border rounded dark:bg-gray-800 dark:text-warm-white ${
                  formErrors.postal_code ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {formErrors.postal_code && (
                <p className="text-red-500 text-sm mt-1">{formErrors.postal_code}</p>
              )}
            </div>

            {/* Country */}
            <div>
              <label htmlFor="country" className="block text-sm font-medium mb-1">
                Country
              </label>
              <input
                id="country"
                type="text"
                value={formData.country || ''}
                onChange={(e) => handleFormChange('country', e.target.value)}
                className={`w-full px-3 py-2 border rounded dark:bg-gray-800 dark:text-warm-white ${
                  formErrors.country ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {formErrors.country && (
                <p className="text-red-500 text-sm mt-1">{formErrors.country}</p>
              )}
            </div>
          </div>

          {/* Form buttons */}
          <div className="flex gap-3 mt-6">
            <button
              onClick={handleFormSubmit}
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
            <button
              onClick={() => {
                setShowForm(false);
                setEditingProfile(null);
                setFormData({});
                setFormErrors({});
              }}
              disabled={saving}
              className="px-4 py-2 bg-gray-300 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-cap-dark rounded-lg p-6 max-w-md">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p className="mb-4">
              Are you sure you want to delete {profiles.find(p => p.contact_id === deleteConfirmId)?.profile_name}?
            </p>
            <div className="flex gap-3">
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                {deleting ? 'Deleting...' : 'Confirm'}
              </button>
              <button
                onClick={() => setDeleteConfirmId(null)}
                disabled={deleting}
                className="px-4 py-2 bg-gray-300 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactProfileManager;